import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { convertAudio, checkTaskStatus } from '../../services/audioApi';
import { 
    Upload, Download, Loader2, AlertCircle, 
    CheckCircle, FileAudio, RefreshCw, ArrowRight
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €"; 

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};

const AudioConverter = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    
    const [file, setFile] = useState(null);
    const [targetFormat, setTargetFormat] = useState('mp3');
    const [error, setError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [progressValue, setProgressValue] = useState(0);
    const [resultUrl, setResultUrl] = useState(null);

    const pollingIntervalRef = useRef(null);
    const formats = ["mp3", "wav", "ogg", "flac", "opus", "webm", "mp4"];

    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        };
    }, []);

    // Polling Logic
    useEffect(() => {
        if (taskId && isProcessing) {
            pollingIntervalRef.current = setInterval(async () => {
                try {
                    const data = await checkTaskStatus(taskId);
                    if (data.state === 'SUCCESS' || data.status === 'completed') {
                        clearInterval(pollingIntervalRef.current);
                        const payload = data.result;
			const innerResult = payload.result;

                        if (innerResult?.new_credits !== undefined) {
                            setCredits(innerResult.new_credits);
                        }
                        if (payload.status === 'completed' && innerResult?.download_url) {
                            setResultUrl(innerResult.download_url);
                            setProgressValue(100);
                            setIsProcessing(false);
                        } else {
                            setError(payload.error || t('audioConverter.errors.generic'));
                            setIsProcessing(false);
                        }
                    } else if (data.state === 'FAILURE') {
                        clearInterval(pollingIntervalRef.current);
                        setError(data.error);
                        setIsProcessing(false);
                    } else if (data.info && data.info.progress) {
                        setProgressValue(data.info.progress);
                    }
                } catch (err) {
                    clearInterval(pollingIntervalRef.current);
                    setError(err.message);
                    setIsProcessing(false);
                }
            }, 2000);
        }
    }, [taskId, isProcessing, t]);

    const handleFileChange = (e) => {
        const selected = e.target.files[0];
        if (selected) {
            if (selected.size > 25 * 1024 * 1024) {
                setError(t('audioConverter.errors.tooLarge'));
                return;
            }
            setFile(selected);
            setError(null);
            setResultUrl(null);
        }
    };

    const handleConvert = async () => {
        if (!file) return;
        setIsProcessing(true);
        setError(null);
        try {
            const response = await convertAudio(file, targetFormat);
            setTaskId(response.task_id);
        } catch (err) {
            setError(err.message);
            setIsProcessing(false);
        }
    };

    const handleReset = () => {
        setFile(null);
        setResultUrl(null);
        setError(null);
        setTaskId(null);
        setProgressValue(0);
    };

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                <SubscribePrompt />
            ) : user?.credits <= 0 ? (
                <PurchaseCreditsPrompt />
            ) : resultUrl ? (
                // SUCCESS STATE
                <div className="max-w-2xl mx-auto text-center mt-10">
                    <div className="bg-green-50 border border-green-200 p-8 rounded-lg mb-8">
                        <CheckCircle className="text-green-500 h-16 w-16 mx-auto mb-4" />
                        <h3 className="text-2xl font-bold text-green-800 mb-2">{t('audioConverter.success')}</h3>
                        <div className="bg-white p-4 rounded-md shadow-sm mt-6">
                            <audio controls src={resultUrl} className="w-full" />
                        </div>
                        <a 
                            href={resultUrl} 
                            download={`converted_audio.${targetFormat}`}
                            className="mt-8 inline-flex items-center px-8 py-3 bg-brand-orange text-white font-bold rounded-md hover:bg-orange-600 transition shadow-lg"
                        >
                            <Download className="mr-2" size={20} /> {t('audioConverter.download')}
                        </a>
                    </div>
                    <button onClick={handleReset} className="text-slate-600 hover:text-brand-orange underline">
                        {t('audioConverter.convertAnother')}
                    </button>
                </div>
            ) : (
                // UPLOAD STATE
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-2xl font-semibold mb-2 text-brand-orange">{t('audioConverter.title')}</h2>
                    <p className="mt-2 text-brand-dark mb-6">{t('audioConverter.description')}</p>

                    <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                        {/* File Upload */}
                        <div className="flex items-center p-8 border-2 border-dashed border-slate-300 rounded-md bg-slate-50 cursor-pointer hover:bg-slate-100 transition relative mb-6">
                             <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer">
                                <Upload className="h-10 w-10 text-slate-400 mb-2" />
                                <span className="text-lg text-brand-orange font-semibold">
                                    {file ? file.name : t('audioConverter.selectFile')}
                                </span>
                                <span className="text-sm text-slate-500 mt-1">{t('audioConverter.maxSize')}</span>
                                <input type="file" className="sr-only" onChange={handleFileChange} accept="audio/*" />
                             </label>
                        </div>

                        {file && (
                            <div className="space-y-6">
                                {/* Format Selection */}
                                <div className="flex flex-col md:flex-row items-center justify-between bg-slate-50 p-4 rounded-lg border border-slate-200 gap-4">
                                    <div className="flex items-center text-slate-700">
                                        <FileAudio className="mr-2" />
                                        <span className="font-medium">{file.name}</span>
                                    </div>
                                    <ArrowRight className="hidden md:block text-slate-400" />
                                    <div className="flex items-center gap-2 w-full md:w-auto">
                                        <label className="whitespace-nowrap font-medium text-slate-700">{t('audioConverter.convertTo')}:</label>
                                        <select 
                                            value={targetFormat} 
                                            onChange={(e) => setTargetFormat(e.target.value)}
                                            className="form-select block w-full border-slate-300 rounded-md shadow-sm focus:border-brand-orange focus:ring focus:ring-brand-orange focus:ring-opacity-50"
                                        >
                                            {formats.map(fmt => (
                                                <option key={fmt} value={fmt}>{fmt.toUpperCase()}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {error && (
                                    <div className="bg-red-50 border border-red-200 p-4 rounded-lg text-red-700 flex items-center">
                                        <AlertCircle className="mr-2" size={20} /> {error}
                                    </div>
                                )}

                                <button
                                    onClick={handleConvert}
                                    disabled={isProcessing}
                                    className="w-full px-8 py-4 bg-brand-orange text-white font-bold text-lg rounded-md hover:bg-orange-600 transition-colors disabled:bg-slate-400 flex items-center justify-center"
                                >
                                    {isProcessing ? (
                                        <><Loader2 className="animate-spin mr-2" /> {t('audioConverter.converting')} {progressValue}%</>
                                    ) : (
                                        <><RefreshCw className="mr-2" /> {t('audioConverter.convertButton')}</>
                                    )}
                                </button>
                                
                                {isProcessing && (
                                    <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                                        <div className="bg-brand-orange h-2 rounded-full transition-all duration-500" style={{ width: `${progressValue}%` }}></div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AudioConverter;
